import { Table, Typography, Row, Col, Divider, message, Button, Space, Tooltip } from "antd"
import React, { useEffect, useState } from "react"

import { DownCircleOutlined, PrinterFilled, UpCircleOutlined } from "@ant-design/icons"
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { Clock } from "../../../../media/icons/Icons"
import VivaPrintingTable from "../../../../components/PaperManager/Machines/VivaPrintingTable"
import { calculateProjectedMeters } from "../../../../components/Orders/Helpers/CalculateProjectedMeters"
import MachineDetails from "../../../../components/PaperManager/Machines/MachineDetails"
import { OrderAndPrefix } from "../../../../components/Orders/Helpers/OrderAndPrefix"

const { Title, Paragraph, Link, Text } = Typography

export default function VivaPaperManager() {
  //loading data for order which are in waiting
  //list for arsoma
  // const { loading, data } = useCollection("orders", ["orderStatus", "==", 2], ["selectedProduct.machine", "==", "Arsoma"]);

  const [waitingList, setWaitingList] = useState([])
  const [tableLoading, setTableLoading] = useState(true)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRowKeysArsoma, setSelectedRowKeysArsoma] = useState([])

  const waitingColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "right",
      filters: [...new Set(waitingList.map((item) => item.client.client))].map((client) => ({
        text: client,
        value: client,
      })),
      onFilter: (value, record) => record.client.client.includes(value),
      filterSearch: true,
      ellipsis: { showTitle: false },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      ellipsis: { showTitle: false },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "labelCount",
      align: "left",
      width: "7%",
      sorter: (a, b) => a.labelCount - b.labelCount,
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "left",
      filters: [...new Set(waitingList.map((item) => item.user.split("@")[0]))].map((user) => ({
        text: user,
        value: user,
      })),
      onFilter: (value, record) => record.user.split("@")[0].includes(value),
      render: (user) => {
        if (typeof user === "string" && user.includes("@")) {
          return user.split("@")[0]
        }
        return user
      },
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "left",
      width: "5%",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "left",
      width: "5%",
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "left",
      filters: [...new Set(waitingList.map((item) => item.selectedProduct.material))].map(
        (material) => ({
          text: material,
          value: material,
        })
      ),
      onFilter: (value, record) => record.selectedProduct.material.includes(value),
      filterSearch: true,
      ellipsis: { showTitle: false },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "left",
      sorter: (a, b) => a.selectedProduct.formatWidth - b.selectedProduct.formatWidth,
      render: (value, record) =>
        record.readRollWidth ? <Text>{record.rollWidth}</Text> : <Text>{value}</Text>,
    },
    {
      title: "Proj. M.",
      dataIndex: "projectedMeters",
      align: "left",
      key: "projectedMeters",
      width: "7%",
      render: (value) => <Text>{value}m</Text>,
    },

    {
      title: "Attachment",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => (
        <Link type="text" rel="noreferrer" target="_blank" href={value}>
          {value ? "Link" : ""}
        </Link>
      ),
    },
    {
      title: "Details",
      align: "center",
      render: (value, record) => <MachineDetails data={record} />,
    },
  ]

  //fetches the documents, then uses the sequence array to sort in correct order
  useEffect(() => {
    const ref = collection(db, "orders")
    let q = query(
      ref,
      where("orderStatus", "==", 2),
      where("selectedProduct.machine", "==", "Viva"),
      where("readyForPrinting", "==", true)
    )

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const data = doc.data()
        results.push({ ...data, key: doc.id, id: doc.id })
      })

      const ordersWithKnifeData = await Promise.all(
        results.map((order) => calculateProjectedMeters(order))
      )

      setWaitingList(ordersWithKnifeData)
      setTableLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const onSelectChange = (newSelectedRowKeys) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys)
    setSelectedRowKeys(newSelectedRowKeys)
  }

  //handles transfer from one table to another one by changing order status
  //takes two parameters, id's from selected rows and number (which indicates where to change)
  const handleBulkTransfer = async (ids, number) => {
    setTableLoading(true)
    try {
      // Create an array of Promises for each update operation
      const updatePromises = ids.map((id) => {
        const orderRef = doc(db, "orders", id)
        return updateDoc(orderRef, {
          orderStatus: number,
        })
      })

      // Wait for all update operations to complete
      await Promise.all(updatePromises)
        .then(() => {
          // Show a success message when all updates are done
          message.success("Updated!")
        })
        .then(() => {
          setTableLoading(false)
          setSelectedRowKeys([])
          setSelectedRowKeysArsoma([])
        })
    } catch (error) {
      // Handle any errors that occurred during the update operations
      console.error("Error updating documents:", error)
      message.error("An error occurred while updating documents.")
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }

  const hasSelected = selectedRowKeys.length > 0

  return (
    <>
      <div style={{ padding: 15 }}>
        <Row gutter={[15, 15]}>
          <Col span={24}>
            <Title level={3}>
              <PrinterFilled />
              <Divider type="vertical" />
              Viva Managment
            </Title>
          </Col>
          <Col span={24}>
            <Paragraph type="secondary">
              This is arsoma printer managment page. You can manage order sequence, change printer
              waiting list and do other things regarding machine managment.
            </Paragraph>
          </Col>

          {/* WAITING LIST */}
          <Col span={24}>
            <Title level={4}>
              <Clock size={16} color="red" />
              <Divider type="vertical" />
              Viva Waiting List
            </Title>
            {hasSelected ? `Selected ${selectedRowKeys.length} Orders` : ""}
            <Table
              style={{ marginTop: "15px" }}
              size="small"
              loading={tableLoading}
              bordered
              columns={waitingColumns}
              dataSource={waitingList}
              rowSelection={rowSelection}
              rowKey={(record) => record.id}
              rowClassName={() => "light-red-row"}
              className="custom-table"
              pagination={{ pageSize: waitingList.length, showSizeChanger: false }}
            />
          </Col>

          {/* BUTTONS */}
          <Col offset={11}>
            <Space>
              <Button type="primary" onClick={() => handleBulkTransfer(selectedRowKeys, 4)}>
                <DownCircleOutlined />
              </Button>
              <Button onClick={() => handleBulkTransfer(selectedRowKeysArsoma, 2)}>
                <UpCircleOutlined />
              </Button>
            </Space>
          </Col>
          <VivaPrintingTable
            selectedRowKeysArsoma={selectedRowKeysArsoma}
            tableLoading={tableLoading}
            setSelectedRowKeysArsoma={setSelectedRowKeysArsoma}
          />
        </Row>
      </div>
    </>
  )
}
